import { ChangeEvent } from "react";
import { FormikErrors } from "formik";
import { TFunction } from "i18next";
import { FieldMetaData } from "../../../types/SchemaFactory";
import { InputType } from "../../../types/InputType";
import QuillInput from "./QuillInput";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";

interface TranslationFieldsRendererProps<TranslationShape extends Record<string, any>> {
    translation: TranslationShape;
    index: number;
    fieldConfigs: Record<string, FieldMetaData>;
    errors?: FormikErrors<TranslationShape>;
    t: TFunction;
    setFieldValue: (fieldName: string, value: string) => void;
    handleFieldChange: (fieldName: string) => (e: ChangeEvent<HTMLInputElement>) => void;
}

const TranslationFieldsRenderer = <TranslationShape extends Record<string, any>>({
    translation,
    index,
    fieldConfigs,
    errors,
    t,
    setFieldValue,
    handleFieldChange,
}: TranslationFieldsRendererProps<TranslationShape>) => {
    return (
        <>
            {Object.entries(fieldConfigs)
                .filter(([, cfg]) => cfg.type !== "hidden")
                .map(([key, cfg]) => {
                    const fieldName = `translations[${index}][${key}]`;
                    const value = translation[key as keyof TranslationShape];
                    const errorKey = errors?.[key as keyof FormikErrors<TranslationShape>];

                    if (cfg.type === "richtext") {
                        return (
                            <div key={key} className="flex flex-col flex-1 bg-white h-full overflow-hidden">
                                <div className="flex-1 h-full">
                                    <QuillInput
                                        value={String(value ?? "")}
                                        onChange={(v: string) => setFieldValue(fieldName, v)}
                                        errorKey={String(errorKey ?? "")}
                                        errorLabel={key}
                                        placeholder={t(cfg.placeholderKey || "")}
                                    />
                                </div>
                            </div>
                        );
                    }

                    return (
                        <CustomInput
                            key={key}
                            type={cfg.type as InputType}
                            labelKey={cfg.placeholderKey}
                            name={key}
                            placeholderKey={cfg.placeholderKey}
                            value={String(value ?? "")}
                            onChange={handleFieldChange(fieldName)}
                            errorKey={String(errorKey ?? "")}
                        />
                    );
                })}
        </>
    );
};

export default TranslationFieldsRenderer;
